import type { Project } from "@openreel/core";
import type { ProjectState } from "../stores/project-store";
import type { SelectionItem } from "../stores/ui-store";
import { trimLinkedCaptions } from "./linked-caption-edit";

export type TimelineItemKind =
  | "media"
  | "text"
  | "shape"
  | "svg"
  | "sticker";

type TimelineActionStore = Pick<
  ProjectState,
  | "getClip"
  | "getTextClip"
  | "getShapeClip"
  | "getSVGClip"
  | "getStickerClip"
  | "removeClip"
  | "deleteTextClip"
  | "deleteShapeClip"
  | "deleteSVGClip"
  | "deleteStickerClip"
  | "duplicateClip"
  | "duplicateOverlayClip"
  | "splitClip"
  | "splitOverlayClip"
  | "trimToPlayhead"
  | "trimOverlayToPlayhead"
  | "project"
  | "getAllTextClips"
  | "updateOverlayClipTiming"
>;

export interface TimelineItemRange {
  id: string;
  trackId: string;
  startTime: number;
  duration: number;
  kind: TimelineItemKind;
}

export interface TimelineMarqueeBounds {
  minTime: number;
  maxTime: number;
  minY: number;
  maxY: number;
}

export function getTimelineItemKind(
  store: Pick<
    TimelineActionStore,
    "getClip" | "getTextClip" | "getShapeClip" | "getSVGClip" | "getStickerClip"
  >,
  clipId: string,
): TimelineItemKind | null {
  if (store.getClip(clipId)) return "media";
  if (store.getTextClip(clipId)) return "text";
  if (store.getShapeClip(clipId)) return "shape";
  if (store.getSVGClip(clipId)) return "svg";
  if (store.getStickerClip(clipId)) return "sticker";
  return null;
}

export function getTimelineSelectionItem(
  store: Pick<
    TimelineActionStore,
    "getClip" | "getTextClip" | "getShapeClip" | "getSVGClip" | "getStickerClip"
  >,
  clipId: string,
): SelectionItem | null {
  const media = store.getClip(clipId);
  if (media) return { id: clipId, trackId: media.trackId, type: "clip" };
  const text = store.getTextClip(clipId);
  if (text) return { id: clipId, trackId: text.trackId, type: "text-clip" };
  const graphic =
    store.getShapeClip(clipId) ||
    store.getSVGClip(clipId) ||
    store.getStickerClip(clipId);
  return graphic
    ? { id: clipId, trackId: graphic.trackId, type: "shape-clip" }
    : null;
}

export async function deleteTimelineItem(
  store: TimelineActionStore,
  clipId: string,
): Promise<boolean> {
  switch (getTimelineItemKind(store, clipId)) {
    case "media":
      return (await store.removeClip(clipId)).success;
    case "text":
      return store.deleteTextClip(clipId);
    case "shape":
      return store.deleteShapeClip(clipId);
    case "svg":
      return store.deleteSVGClip(clipId);
    case "sticker":
      return store.deleteStickerClip(clipId);
    default:
      return false;
  }
}

export async function duplicateTimelineItem(
  store: TimelineActionStore,
  clipId: string,
): Promise<boolean> {
  if (getTimelineItemKind(store, clipId) === "media") {
    return (await store.duplicateClip(clipId)).success;
  }
  return store.duplicateOverlayClip(clipId) !== null;
}

export async function splitTimelineItem(
  store: TimelineActionStore,
  clipId: string,
  time: number,
): Promise<boolean> {
  if (getTimelineItemKind(store, clipId) === "media") {
    return (await store.splitClip(clipId, time)).success;
  }
  return store.splitOverlayClip(clipId, time) !== null;
}

export async function trimTimelineItemToPlayhead(
  store: TimelineActionStore,
  clipId: string,
  playheadTime: number,
  trimStart: boolean,
): Promise<boolean> {
  if (getTimelineItemKind(store, clipId) === "media") {
    const sourceClip = store.getClip(clipId);
    const result = await store.trimToPlayhead(clipId, playheadTime, trimStart);
    const updatedClip = store.getClip(clipId);
    if (result.success && sourceClip && updatedClip) {
      trimLinkedCaptions(
        store,
        sourceClip,
        updatedClip.startTime,
        updatedClip.startTime + updatedClip.duration,
      );
    }
    return result.success;
  }
  return store.trimOverlayToPlayhead(clipId, playheadTime, trimStart) !== null;
}

export function getTimelineItemRanges(project: Project): TimelineItemRange[] {
  const items: TimelineItemRange[] = [];

  for (const track of project.timeline.tracks) {
    for (const clip of track.clips) {
      items.push({
        id: clip.id,
        trackId: track.id,
        startTime: clip.startTime,
        duration: clip.duration,
        kind: "media",
      });
    }
  }

  const append = (
    clips: ReadonlyArray<{
      id: string;
      trackId: string;
      startTime: number;
      duration: number;
    }> | undefined,
    kind: Exclude<TimelineItemKind, "media">,
  ) => {
    for (const clip of clips ?? []) {
      items.push({ ...clip, kind });
    }
  };

  append(project.textClips, "text");
  append(project.shapeClips, "shape");
  append(project.svgClips, "svg");
  append(project.stickerClips, "sticker");

  return items;
}

export interface SplittableOptions {
  /**
   * With nothing selected, act on every unlocked clip under the playhead
   * instead of on nothing. Split turns this on: pressing S over a clip and
   * getting silence until it is clicked first cost two gestures per cut.
   * Q/W trim-to-playhead deliberately does not: trimming everything under
   * the playhead across tracks is too much action for an unmodified key.
   */
  fallbackToPlayhead?: boolean;
  /**
   * Blade every unlocked clip under the playhead whether or not something is
   * selected. This is the difference between the two blades: plain S obeys a
   * selection when there is one (fallbackToPlayhead only rescues the empty
   * case), while the all-tracks blade is an explicit "cut through
   * everything" and must not quietly narrow to the selection.
   */
  ignoreSelection?: boolean;
}

/**
 * The half of the question that does not depend on where the playhead is.
 *
 * Locked tracks and the selection rule hold still for as long as the project
 * and the selection do; the playhead moves every animation frame. Split into
 * its own function so a caller asking per tick can pay for this once per edit:
 * the combined version built a range object for every clip in the project and
 * filtered it twice, on every move of a drag and every frame of playback.
 */
export function getSplittableCandidates(
  project: Project,
  selectedIds: readonly string[],
  options?: SplittableOptions,
): TimelineItemRange[] {
  const selected = new Set(selectedIds);
  const lockedTracks = new Set(
    project.timeline.tracks.filter((t) => t.locked).map((t) => t.id),
  );
  const takeAll =
    options?.ignoreSelection ||
    (selected.size === 0 && options?.fallbackToPlayhead);
  return getTimelineItemRanges(project).filter(
    (item) =>
      !lockedTracks.has(item.trackId) && (takeAll || selected.has(item.id)),
  );
}

/**
 * Strictly inside, never on an edge. A playhead sitting exactly on a boundary
 * is already a cut there, and splitting would mint a zero-length fragment.
 */
function coversTime(item: TimelineItemRange, time: number): boolean {
  return time > item.startTime && time < item.startTime + item.duration;
}

/**
 * Whether anything is splittable at `time`, without building the id list.
 *
 * This is what a per-tick gate wants: the answer is a boolean, so allocating an
 * array of ids to measure its length is pure waste at 60 times a second.
 */
export function hasSplittableItemAtTime(
  candidates: readonly TimelineItemRange[],
  time: number,
): boolean {
  return candidates.some((item) => coversTime(item, time));
}

/** Which of the candidates the playhead is inside, in timeline item order. */
export function splittableIdsAtTime(
  candidates: readonly TimelineItemRange[],
  time: number,
): string[] {
  const ids: string[] = [];
  for (const item of candidates) {
    if (coversTime(item, time)) ids.push(item.id);
  }
  return ids;
}

/**
 * Both halves at once, for the callers that ask a handful of times per gesture
 * rather than per tick.
 */
export function getSplittableTimelineItemIds(
  project: Project,
  selectedIds: readonly string[],
  time: number,
  options?: SplittableOptions,
): string[] {
  return splittableIdsAtTime(
    getSplittableCandidates(project, selectedIds, options),
    time,
  );
}

// ─── In/out range ──────────────────────────────────────────────────
// A marked span of timeline seconds. Kept structurally identical to
// ui-store's InOutRange so the two can be passed around interchangeably
// without this module importing store state.

export interface TimeRange {
  start: number;
  end: number;
}

/**
 * Items a marked range touches.
 *
 * "overlap" is the selection sense (anything the range crosses at all);
 * "contained" is the delete sense (only what lies wholly inside, so an
 * extract does not silently eat the tails of clips it merely clipped).
 * Zero-length ranges match nothing in either mode.
 */
export function getTimelineItemsInRange(
  project: Project,
  range: TimeRange,
  options?: { mode?: "overlap" | "contained"; includeLocked?: boolean },
): TimelineItemRange[] {
  if (!(range.end > range.start)) return [];

  const lockedTracks = new Set(
    project.timeline.tracks.filter((t) => t.locked).map((t) => t.id),
  );
  const contained = options?.mode === "contained";

  return getTimelineItemRanges(project).filter((item) => {
    if (!options?.includeLocked && lockedTracks.has(item.trackId)) return false;
    const itemEnd = item.startTime + item.duration;
    return contained
      ? item.startTime >= range.start && itemEnd <= range.end
      : item.startTime < range.end && itemEnd > range.start;
  });
}

/**
 * Where a range edit has to cut before it can lift or extract.
 *
 * Each mark only cuts the items it lands strictly inside, which is the same
 * strict predicate the blade uses: a mark sitting exactly on an edge is
 * already a cut, and splitting there would mint a zero-length fragment.
 */
export function planRangeSplitPoints(
  project: Project,
  range: TimeRange,
): Array<{ id: string; time: number }> {
  if (!(range.end > range.start)) return [];

  const cuts: Array<{ id: string; time: number }> = [];
  for (const time of [range.start, range.end]) {
    for (const id of getSplittableTimelineItemIds(project, [], time, {
      fallbackToPlayhead: true,
    })) {
      cuts.push({ id, time });
    }
  }
  return cuts;
}

// ─── Ripple model ──────────────────────────────────────────────────
// The pure half: given a project and an edit, say exactly what moves and by
// how much. Nothing here touches a store, so both the interactive gestures
// and the programmatic callers (silence cut, highlight apply) can share one
// definition of what "ripple" means instead of each inventing their own.

export interface RippleShift {
  id: string;
  trackId: string;
  kind: TimelineItemKind;
  fromStartTime: number;
  toStartTime: number;
}

export interface RipplePlan {
  /** Seconds every shifted item moves. Negative closes a gap. */
  delta: number;
  /** The edit point items are measured against. */
  fromTime: number;
  shifts: RippleShift[];
}

export interface RippleScope {
  /**
   * Magnet mode. False ripples only the edited clip's own track, which is what
   * the shipped clip/rippleDelete action does and what silence-cut relies on.
   * True ripples every unlocked track, which keeps cross-track material in
   * sync but is a wider edit than anything in the engine does today.
   */
  allTracks?: boolean;
  /** Items that must not move, normally the clip being edited. */
  excludeIds?: readonly string[];
}

const EMPTY_PLAN = (fromTime: number): RipplePlan => ({
  delta: 0,
  fromTime,
  shifts: [],
});

/**
 * The primitive every other planner reduces to: shift what sits after
 * `fromTime` by `delta`.
 *
 * A negative delta is clamped for the plan as a whole rather than per item.
 * Clamping each item to zero independently would compress the material nearest
 * the timeline start while everything behind it kept its full shift, silently
 * destroying the relative spacing a ripple exists to preserve. Better to move
 * everything less far and leave the gap partly open.
 */
export function planRipple(
  project: Project,
  edit: {
    trackId: string | null;
    fromTime: number;
    delta: number;
    inclusive?: boolean;
  },
  scope: RippleScope = {},
): RipplePlan {
  if (edit.delta === 0) return EMPTY_PLAN(edit.fromTime);

  const lockedTracks = new Set(
    project.timeline.tracks.filter((t) => t.locked).map((t) => t.id),
  );
  const excluded = new Set(scope.excludeIds ?? []);

  const movers = getTimelineItemRanges(project).filter((item) => {
    if (excluded.has(item.id)) return false;
    if (lockedTracks.has(item.trackId)) return false;
    if (!scope.allTracks && item.trackId !== edit.trackId) return false;
    return edit.inclusive
      ? item.startTime >= edit.fromTime
      : item.startTime > edit.fromTime;
  });

  if (movers.length === 0) return EMPTY_PLAN(edit.fromTime);

  const earliest = movers.reduce(
    (min, item) => Math.min(min, item.startTime),
    Infinity,
  );
  const delta = edit.delta < 0 ? Math.max(edit.delta, -earliest) : edit.delta;

  if (delta === 0) return EMPTY_PLAN(edit.fromTime);

  return {
    delta,
    fromTime: edit.fromTime,
    shifts: movers.map((item) => ({
      id: item.id,
      trackId: item.trackId,
      kind: item.kind,
      fromStartTime: item.startTime,
      toStartTime: item.startTime + delta,
    })),
  };
}

/**
 * What closes up behind a clip that is about to be removed.
 *
 * The edit point is the clip's OUT point, not its start. On the clip's own
 * track the two are interchangeable, because nothing else on a track starts
 * inside a clip, which is why this reproduces the shipped clip/rippleDelete
 * exactly. Across tracks they are not: measuring from the start would sweep up
 * any clip that merely begins somewhere inside the deleted span and, since the
 * plan clamps as a whole, a single such clip near time zero would throttle the
 * ripple for everything else. Material that straddles the deleted span is left
 * alone here; shortening it is a trim, which a shift plan cannot express.
 */
export function planRippleDelete(
  project: Project,
  itemId: string,
  scope: RippleScope = {},
): RipplePlan | null {
  const item = findItem(project, itemId);
  if (!item) return null;

  return planRipple(
    project,
    {
      trackId: item.trackId,
      fromTime: item.startTime + item.duration,
      delta: -item.duration,
      inclusive: true,
    },
    { ...scope, excludeIds: [...(scope.excludeIds ?? []), itemId] },
  );
}

/**
 * What follows a trim that changed a clip's extent.
 *
 * Measured on the out point, so a left-edge trim (which moves startTime and
 * duration together, the way the interactive handles do) and a right-edge trim
 * both produce the shift the material after them actually needs.
 */
export function planRippleTrim(
  project: Project,
  itemId: string,
  next: { startTime: number; duration: number },
  scope: RippleScope = {},
): RipplePlan | null {
  const item = findItem(project, itemId);
  if (!item) return null;

  const outPoint = item.startTime + item.duration;
  const delta = next.startTime + next.duration - outPoint;

  return planRipple(
    project,
    { trackId: item.trackId, fromTime: outPoint, delta, inclusive: true },
    { ...scope, excludeIds: [...(scope.excludeIds ?? []), itemId] },
  );
}

/**
 * What moves aside to open room for a clip being inserted at `atTime`.
 *
 * Inclusive, because the clip that has to move is normally the one starting
 * exactly on the insertion point: "dropped between two clips" means dropped on
 * the second one's start, and the exclusive test would leave the new clip
 * sitting on top of it. planRippleDelete sets inclusive for the mirror-image
 * reason.
 *
 * A positive delta also means planRipple's clamp cannot touch this plan: the
 * clamp only fires for a negative delta, so the shift is always exactly the
 * room the incoming clip needs, never less.
 */
export function planRippleInsert(
  project: Project,
  insert: { trackId: string | null; atTime: number; duration: number },
  scope: RippleScope = {},
): RipplePlan {
  if (!(insert.duration > 0)) return EMPTY_PLAN(insert.atTime);

  return planRipple(
    project,
    {
      trackId: insert.trackId,
      fromTime: insert.atTime,
      delta: insert.duration,
      inclusive: true,
    },
    scope,
  );
}

/** What closes up behind a marked range that is about to be extracted. */
export function planRangeRipple(
  project: Project,
  range: TimeRange,
  scope: RippleScope = {},
): RipplePlan {
  if (!(range.end > range.start)) return EMPTY_PLAN(range.start);

  return planRipple(
    project,
    {
      trackId: null,
      fromTime: range.end,
      delta: -(range.end - range.start),
      inclusive: true,
    },
    // A range is a span of the timeline, not a clip on one track, so it always
    // ripples everything: leaving other tracks put would slide the whole
    // project out of sync with itself.
    { ...scope, allTracks: true },
  );
}

/**
 * Commits a plan. Media clips go through moveClips so the whole ripple is one
 * history entry; overlays have no batch API and are updated one by one inside
 * the same history group.
 */
export async function applyRipplePlan(
  store: Pick<
    ProjectState,
    | "moveClips"
    | "updateOverlayClipTiming"
    | "beginHistoryGroup"
    | "endHistoryGroup"
  >,
  plan: RipplePlan,
  description = "Ripple",
): Promise<boolean> {
  if (plan.shifts.length === 0) return false;

  const media = plan.shifts.filter((shift) => shift.kind === "media");
  const overlays = plan.shifts.filter((shift) => shift.kind !== "media");

  store.beginHistoryGroup(description);
  try {
    if (media.length > 0) {
      const result = await store.moveClips(
        media.map((shift) => ({
          clipId: shift.id,
          startTime: shift.toStartTime,
        })),
      );
      if (!result.success) return false;
    }
    for (const shift of overlays) {
      store.updateOverlayClipTiming(shift.id, {
        startTime: shift.toStartTime,
      });
    }
  } finally {
    store.endHistoryGroup();
  }

  return true;
}

function findItem(project: Project, itemId: string): TimelineItemRange | null {
  return getTimelineItemRanges(project).find((i) => i.id === itemId) ?? null;
}

export function getTimelineSelectionItems(project: Project): SelectionItem[] {
  return getTimelineItemRanges(project).map(toSelectionItem);
}

/** Select every media or overlay clip assigned to one timeline track. */
export function getTimelineTrackSelection(
  project: Project,
  trackId: string,
): SelectionItem[] {
  return getTimelineItemRanges(project)
    .filter((item) => item.trackId === trackId)
    .sort((left, right) =>
      left.startTime === right.startTime
        ? left.id.localeCompare(right.id)
        : left.startTime - right.startTime,
    )
    .map(toSelectionItem);
}

function toSelectionItem(item: TimelineItemRange): SelectionItem {
  return {
    id: item.id,
    trackId: item.trackId,
    type: item.kind === "media" ? "clip" : item.kind === "text" ? "text-clip" : "shape-clip",
  };
}

export function getTimelineMarqueeSelection(
  project: Project,
  bounds: TimelineMarqueeBounds,
  getTrackHeight: (
    trackId: string,
    trackType: Project["timeline"]["tracks"][number]["type"],
  ) => number,
): SelectionItem[] {
  const itemsByTrack = new Map<string, TimelineItemRange[]>();
  for (const item of getTimelineItemRanges(project)) {
    const items = itemsByTrack.get(item.trackId) ?? [];
    items.push(item);
    itemsByTrack.set(item.trackId, items);
  }

  const selected: SelectionItem[] = [];
  let currentY = 0;
  for (const track of project.timeline.tracks) {
    const height = getTrackHeight(track.id, track.type);
    const trackOverlaps =
      bounds.minY < currentY + height && bounds.maxY > currentY;
    if (trackOverlaps) {
      for (const item of itemsByTrack.get(track.id) ?? []) {
        const itemEnd = item.startTime + item.duration;
        if (bounds.minTime < itemEnd && bounds.maxTime > item.startTime) {
          selected.push({
            id: item.id,
            trackId: item.trackId,
            type:
              item.kind === "media"
                ? "clip"
                : item.kind === "text"
                  ? "text-clip"
                  : "shape-clip",
          });
        }
      }
    }
    currentY += height;
  }
  return selected;
}
